import React, { createContext, useContext } from 'react';
import { primary } from 'apps/sigma2-ui/lib/design-tokens/nivo-themes';
import { RadarChart } from 'apps/sigma2-ui/src/components/RadarChart';
import { RadarSliceTooltipProps } from '@nivo/radar';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { DefaultCalibrationLevelDataPoint } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionChartType';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Percentage } from 'apps/sigma2-ui/src/components/Percentage';
import { DefaultCalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { adjustCalibrationScoreForGraph } from 'apps/sigma2-ui/src/lib/graph-helpers';

interface DefaultPolarChartProps {
  dataPoints: DefaultCalibrationLevelDataPoint[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}

type PolarChartProps = DefaultPolarChartProps;

interface DefaultPolarChartContext {
  dataPoints: DefaultCalibrationLevelDataPoint[];
  calibrationFraming: CalibrationFraming;
}

const DefaultPolarChartContext = createContext<DefaultPolarChartContext>({
  dataPoints: [],
  calibrationFraming: DefaultCalibrationFraming,
});

const getDataPointColorByNumber = (
  value: number,
  calibrationFraming: CalibrationFraming,
) => {
  const numberToHexMapping: Record<number, string> = {
    6: calibrationFraming === 'bias' ? '#EE3F3F' : '#809EF4',
    5: calibrationFraming === 'bias' ? '#EE3F3F' : '#9696FA',
    4: calibrationFraming === 'bias' ? '#F671B3' : '#A890F1',
    3: calibrationFraming === 'bias' ? '#BF89F5' : '#BF89F5',
    2: calibrationFraming === 'bias' ? '#A890F1' : '#F671B3',
    1: calibrationFraming === 'bias' ? '#9696FA' : '#EE3F3F',
    0: calibrationFraming === 'bias' ? '#809EF4' : '#EE3F3F',
  };

  const calibrationScoreRounded = Math.floor(value);
  return numberToHexMapping[calibrationScoreRounded];
};

const BiasLevelRadarTooltip = ({ index }: RadarSliceTooltipProps) => {
  const context = useContext(DefaultPolarChartContext);
  const language = useLanguage();

  // Pulls in PolarDataPoint[] from context
  const levelData = context.dataPoints.find((x) => x.id === index);

  if (!levelData) {
    return null;
  }

  const calibrationLevel = getCalibrationLevel(levelData.calibrationScore);

  const formatMoneyRange = (value: number) => {
    const min = Math.floor(value / 1000);
    const max = min + 1;
    return `$${min}k to $${max}k`;
  };

  const formatMoney = (value: number) => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <div
      className="filter flex flex-col gap-1 shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md"
      data-qa={`tooltip${index}`}
    >
      <p
        className="text-xl font-bold font-sans"
        data-qa={`tooltip${index}Title`}
      >
        {index}
      </p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        data-qa={`tooltip${index}Pill`}
        calibrationFraming={context.calibrationFraming}
      />
      <div>
        <p>{language.payCorrectionTooltipPayGap()}</p>
        <p data-qa={`tooltip${index}PayGap`}>
          <Percentage percentage={levelData.disparity} />
        </p>
      </div>
      <div>
        <p>{language.payCorrectionTooltipNeedsAttention()}</p>
        <p data-qa={`tooltip${index}NeedsAttention`}>
          {levelData.needsAttention} employees
        </p>
      </div>
      <div>
        <p>{language.payCorrectionTooltipOnAverageUnderpaid()}</p>
        <p data-qa={`tooltip${index}OnAverageUnderpaid`}>
          {formatMoneyRange(levelData.avgUnderpaid)}
        </p>
      </div>
      <div>
        <p>{language.payCorrectionTooltipBudgetNeeded()}</p>
        <p data-qa={`tooltip${index}BudgetNeeded`}>
          {formatMoney(levelData.budgetNeeded)}
        </p>
      </div>
    </div>
  );
};

const DefaultPolarChart = (props: {
  dataPoints: DefaultCalibrationLevelDataPoint[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}) => {
  const { dataPoints, calibrationFraming } = props;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '100%',
        aspectRatio: '1/1',
      }}
      className=""
    >
      <RadarChart
        theme={primary}
        borderColor={'#400977'}
        curve={'catmullRomClosed'}
        data={dataPoints.map((dataPoint) => ({
          ...dataPoint,
          adjustedScore: adjustCalibrationScoreForGraph(
            dataPoint.calibrationScore,
            calibrationFraming,
          ),
        }))}
        dataTestId={props.dataTestId}
        maxValue={6}
        gridLevels={6}
        gridShape={'circular'}
        indexBy={'id'}
        valueFormat={'>-.2f'}
        keys={['adjustedScore']}
        layers={['grid', 'layers', 'slices', 'dots', 'legends']}
        motionConfig={'default'}
        dotSize={12}
        dotColor={(dataPoint) =>
          getDataPointColorByNumber(dataPoint.value, calibrationFraming)
        }
        dotBorderColor={'#400977'}
        dotBorderWidth={2}
        gradientFills={[
          calibrationFraming === 'bias'
            ? 'radialGradientFigmaPurpleBias'
            : 'radialGradientFigmaPurpleOptimization',
        ]}
        levelLabelsConfig={{
          showLevelLabels: true,
          fontSize: '14px',
        }}
        sliceTooltip={BiasLevelRadarTooltip}
        animate={true}
      />
    </div>
  );
};

export const CalibrationLevelRadarChart = ({
  dataPoints,
  calibrationFraming,
  dataTestId,
}: PolarChartProps) => {
  return (
    <DefaultPolarChartContext.Provider
      value={{ dataPoints, calibrationFraming }}
    >
      <DefaultPolarChart
        dataPoints={dataPoints}
        calibrationFraming={calibrationFraming}
        dataTestId={dataTestId}
      />
    </DefaultPolarChartContext.Provider>
  );
};
